/* eslint-disable @typescript-eslint/unbound-method */
import React from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
    AppBar,
    Box,
    Button,
    Step,
    StepButton,
    StepLabel,
    Stepper,
    Toolbar,
    Typography,
    styled
} from '@mui/material';
import Left from '@mui/icons-material/KeyboardArrowLeft';
import { AudienceCreateModal } from 'components/audience/modals/AudienceCreateModal';
import { Audience } from 'components/audience/models/segmentModel';
import { campaignApi } from 'components/campaigns/campaignApi';
import {
    CampaignDefineFormData,
    CreateCampaignDefine
} from 'components/campaigns/forms/CreateCampaignDefine';
import { CreateCampaignReview } from 'components/campaigns/forms/CreateCampaignReview';
import { CreateCampaignSettings } from 'components/campaigns/forms/CreateCampaignSettings';
import { CampaignItem } from 'components/campaigns/models/CampaignItem';
import { Perk } from 'components/loyalty/models/PerkModel';
import { PerksCreationModal } from 'components/perks/modals/PerksCreationModal';
import {
    MESSAGE_MARKETING_CAMPAIGN_SAVE_ERROR,
    MESSAGE_MARKETING_CAMPAIGN_SAVE_SUCCESS
} from 'config/messages';
import { MARKETING_CAMPAIGNS } from 'config/routes';
import logger from 'lib/logger';
import Logo from 'static/pepper-logo-white.png';
import { getAllAudiences } from 'store/audiences/audiencesActions';
import {
    closeCampaignDiscardModals,
    closeCampaignModals,
    discardCampaignAudienceModal,
    discardCampaignPerkModal,
    loadCampaign,
    submitCampaignItem,
    submitCampaignItemAction,
    updateCampaignEffectTarget
} from 'store/campaigns/campaignsAction';
import { CampaignsActionType } from 'store/campaigns/campaignsActionTypes';
import { getAllLocations } from 'store/locations/locationsActions';
import { getCurrencySymbol } from 'store/manifest/manifestAction';
import { enqueueSnackbar } from 'store/notifications/notificationsActions';
import { getAllPerks } from 'store/perks/perksActions';
import { PerksActionType } from 'store/perks/perksActionTypes';
import { ApplicationState } from 'store/store';
import { ConfirmDialog } from 'ui/dialogs/ConfirmDialog';
import { Column, Row } from 'ui/Flex';

const HEADER_HEIGHT = 100;

const PREFIX = 'campaigns-create';

const classes = {
    appBar: `${PREFIX}-appBar`,
    toolBar: `${PREFIX}-toolBar`,
    row: `${PREFIX}-row`,
    logo: `${PREFIX}-logo`,
    label: `${PREFIX}-label`,
    campaignName: `${PREFIX}-campaignName`
};
const StyledColumn = styled(Column)(({ theme }) => ({
    height: '100%',
    width: '100%',
    backgroundColor: theme.palette.background.default,
    [`& .${classes.appBar}`]: {
        top: 0,
        height: HEADER_HEIGHT
    },
    [`& .${classes.toolBar}`]: {
        height: HEADER_HEIGHT,
        minHeight: 'unset',
        zIndex: 100,
        paddingLeft: theme.spacing(3),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingRight: 0,
        position: 'relative'
    },
    [`& .${classes.logo}`]: {
        width: theme.spacing(12.5),
        marginBottom: theme.spacing(2)
    },
    [`& .${classes.row}`]: {
        height: '100%',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
    },
    [`& .${classes.label}`]: {
        fontSize: '10px',
        fontWeight: 500,
        letterSpacing: '1.5px',
        color: theme.palette.common.white,
        textTransform: 'uppercase'
    },
    [`& .${classes.campaignName}`]: {
        fontSize: '20px',
        fontWeight: 600,
        letterSpacing: '0.15px',
        color: theme.palette.common.white
    }
}));

const steps = ['Define', 'Configure', 'Review'];

export default () => {
    const history = useHistory();
    const params = useParams<{ campaignId?: string }>();
    const dispatch = useDispatch();
    const [activeStep, setActiveStep] = React.useState(0);
    const [backModalOpen, setBackModalOpen] = React.useState(false);

    // Calculated
    const isCampaignExist = React.useMemo(() => !!params.campaignId, [params.campaignId]);

    // Redux state
    const store = useStore();
    const audiences = useSelector((state: ApplicationState) => state.audiences.customerSegments);
    const perks = useSelector((state: ApplicationState) => state.perks.perks);
    const currencySymbol = useSelector((state: ApplicationState) => state.manifest.currencySymbol);
    const campaignItems = useSelector((state: ApplicationState) => state.campaigns.campaignItems);
    const {
        createAudienceModalIndex,
        createPerkModalIndex,
        createAudienceModalTitle,
        currentCampaign,
        createPerkModalTitle,
        discardAudienceCreationOpen,
        discardPerkCreationOpen,
        discardVoucherCreationOpen,
        isLoading
    } = useSelector((state: ApplicationState) => state.campaigns);
    const campaignId = currentCampaign && currentCampaign.id;

    const handleStepClick = React.useCallback((index: number) => {
        setActiveStep(index);
    }, []);
    const handleBackClick = React.useCallback(() => {
        setBackModalOpen(true);
    }, []);
    const handleBackClose = React.useCallback(() => {
        setBackModalOpen(false);
    }, []);
    const handleDiscardAudienceCreation = React.useCallback(() => {
        discardCampaignAudienceModal()(dispatch);
    }, [dispatch]);
    const handleDiscardPerkCreaton = React.useCallback(() => {
        discardCampaignPerkModal()(dispatch);
    }, [dispatch]);
    const handleDiscardCancel = React.useCallback(() => {
        closeCampaignDiscardModals()(dispatch);
    }, [dispatch]);
    const handleDiscardAccept = React.useCallback(() => {
        closeCampaignModals()(dispatch);
    }, [dispatch]);

    const acceptBack = React.useCallback(() => {
        history.push(MARKETING_CAMPAIGNS);
    }, [history]);

    const handleDefineEdit = React.useCallback(() => {
        setActiveStep(0);
    }, []);
    const handleNext = React.useCallback(() => {
        setActiveStep(activeStep + 1);
    }, [activeStep]);
    const handleBack = React.useCallback(() => {
        setActiveStep(activeStep - 1);
    }, [activeStep]);

    React.useEffect(() => {
        if (params.campaignId) {
            loadCampaign(params.campaignId, () => history.push(MARKETING_CAMPAIGNS))(dispatch);
        }
    }, [params.campaignId, dispatch, history]);

    React.useEffect(() => {
        getAllAudiences()(dispatch);
        getCurrencySymbol()(dispatch);
        getAllPerks()(dispatch);
        getAllLocations()(dispatch);
    }, [dispatch]);

    const handlePerkCreateSuccess = React.useCallback(
        ({ perk }: { perk: Perk }) => {
            dispatch({
                perk,
                type: PerksActionType.CREATE_SUCCESS
            });
            updateCampaignEffectTarget(perk._id)(dispatch);
            submitCampaignItem(createPerkModalIndex)(dispatch, store.getState);
            closeCampaignModals()(dispatch);
        },
        [dispatch, store.getState, createPerkModalIndex]
    );
    const handleAudienceCreateSuccess = React.useCallback(
        (audience: Audience) => {
            updateCampaignEffectTarget(audience._id)(dispatch);
            submitCampaignItem(createAudienceModalIndex)(dispatch, store.getState);
            closeCampaignModals()(dispatch);
        },
        [dispatch, store.getState, createAudienceModalIndex]
    );

    const handleSubmitDefine = React.useCallback(
        async (values: CampaignDefineFormData) => {
            dispatch({
                type: CampaignsActionType.START_REQUEST
            });
            try {
                const result = await (currentCampaign && currentCampaign.id
                    ? campaignApi.update(currentCampaign.id, { body: values })
                    : campaignApi.create({ body: values }));
                if (!result.ok) {
                    throw new Error(result.body.message);
                }
                dispatch({
                    type: CampaignsActionType.CREATE_CAMPAIGN_SUCCESS,
                    campaign: { ...values, id: result.body.id }
                });
                dispatch({
                    type: CampaignsActionType.END_REQUEST
                });
                dispatch(enqueueSnackbar(MESSAGE_MARKETING_CAMPAIGN_SAVE_SUCCESS, { variant: 'info' }));
                setActiveStep(activeStep + 1);
            } catch (e) {
                dispatch({
                    type: CampaignsActionType.END_REQUEST
                });
                logger.error(e.message);
                dispatch(enqueueSnackbar(MESSAGE_MARKETING_CAMPAIGN_SAVE_ERROR, { variant: 'error' }));
            }
        },
        [activeStep, dispatch, currentCampaign]
    );
    const handleSettingsEdit = React.useCallback(() => {
        setActiveStep(1);
    }, []);

    const handleItemSubmit = React.useCallback(
        (item: CampaignItem, index: number) => {
            dispatch({
                index,
                campaignItem: item,
                type: CampaignsActionType.UPDATE_CAMPAIGN_ITEM
            });
            submitCampaignItemAction(item, index, campaignId, perks)(dispatch);
        },
        [campaignId, dispatch, perks]
    );

    const getHandleStepClick = React.useCallback(index => () => handleStepClick(index), [handleStepClick]);

    return (
        <StyledColumn>
            <Row valign="flex-start" align="center">
                <Box flex={1} maxWidth={500} marginTop={HEADER_HEIGHT / 8}>
                    <Stepper nonLinear alternativeLabel activeStep={activeStep}>
                        {steps.map((item, index) => (
                            <Step
                                key={`step-button-${item}`}
                                completed={isCampaignExist || activeStep > index}
                            >
                                <StepButton
                                    disableRipple
                                    disabled={!isCampaignExist && activeStep < index}
                                    onClick={getHandleStepClick(index)}
                                    color="inherit"
                                >
                                    <StepLabel>{item}</StepLabel>
                                </StepButton>
                            </Step>
                        ))}
                    </Stepper>
                </Box>
            </Row>
            <CreateCampaignDefine
                isCampaignExist={isCampaignExist}
                onSubmit={handleSubmitDefine}
                initialValues={currentCampaign}
                open={activeStep === 0}
                isLoading={isLoading}
            />
            <CreateCampaignSettings
                open={activeStep === 1}
                audiences={audiences}
                perks={perks}
                onItemSubmit={handleItemSubmit}
                onSubmit={handleNext}
                isLoading={isLoading}
            />
            {activeStep === 2 && (
                <CreateCampaignReview
                    onEdit={handleDefineEdit}
                    defineData={currentCampaign}
                    onSettingsEdit={handleSettingsEdit}
                    settingsData={campaignItems}
                    onContinue={acceptBack}
                    isLoading={isLoading}
                    onBack={handleBack}
                />
            )}
            <AppBar className={classes.appBar} position="fixed" color="secondary">
                <Toolbar className={classes.toolBar}>
                    <Column valign="space-between" align="flex-start" flex={1}>
                        <img className={classes.logo} src={Logo} alt="" />
                        <Button variant="text" color="primary" startIcon={<Left />} onClick={handleBackClick}>
                            Back to campaigns
                        </Button>
                    </Column>
                    <Column align="center" valign="center" className={classes.row}>
                        <Typography className={classes.label}>
                            {isCampaignExist ? 'Edit' : 'Create'} a campaign
                        </Typography>
                        <Typography className={classes.campaignName}>
                            {currentCampaign && currentCampaign.title}
                        </Typography>
                    </Column>
                </Toolbar>
            </AppBar>
            <ConfirmDialog
                confirmLabel="Yes, Discard"
                cancelLabel="No, continue editing"
                title="Are you sure you want to discard this campaign?"
                content="All changes you made will be lost. If you want to save your changes, use the Save For Later button."
                open={backModalOpen}
                onConfirm={acceptBack}
                onCancel={handleBackClose}
            />
            <ConfirmDialog
                confirmLabel="Yes, Cancel"
                cancelLabel="No, don't cancel"
                title="Hold On!"
                content="Are you sure you want to cancel this perk creation process? All changes you made will be lost."
                open={discardPerkCreationOpen}
                onConfirm={handleDiscardAccept}
                onCancel={handleDiscardCancel}
            />
            <ConfirmDialog
                confirmLabel="Yes, Cancel"
                cancelLabel="No, don't cancel"
                title="Hold On!"
                content="Are you sure you want to cancel this audience creation process? All changes you made will be lost."
                open={discardAudienceCreationOpen}
                onConfirm={handleDiscardAccept}
                onCancel={handleDiscardCancel}
            />
            <ConfirmDialog
                confirmLabel="Yes, Cancel"
                cancelLabel="No, don't cancel"
                title="Hold On!"
                content="Are you sure you want to cancel this voucher creation process? All changes you made will be lost."
                open={discardVoucherCreationOpen}
                onConfirm={handleDiscardAccept}
                onCancel={handleDiscardCancel}
            />
            <PerksCreationModal
                initialGeneralData={{ title: createPerkModalTitle }}
                currencySymbol={currencySymbol}
                open={createPerkModalIndex > -1}
                onClose={handleDiscardPerkCreaton}
                onSubmit={handlePerkCreateSuccess}
            />
            <AudienceCreateModal
                initialData={{ title: createAudienceModalTitle }}
                open={createAudienceModalIndex > -1}
                onClose={handleDiscardAudienceCreation}
                onSuccess={handleAudienceCreateSuccess}
            />
        </StyledColumn>
    );
};
